import { DateTime } from "luxon";
import React, { useEffect, useMemo } from "react";
import { useSelector } from "react-redux";
import { useImmer } from "use-immer";

import { AnalyzeResourceResponseBodyType } from "@intelligems/ig-types";
import { Grid } from "@mui/material";
import { UseQueryResult } from "@tanstack/react-query";

import { useSitewideAnalysis } from "../../../../api/analysis/analysis";
import {
  filterDashboardData,
  getGraphConfig,
} from "../../../../api/formatters/dashboard";
import { NoExperimentDataOverlay } from "../../../../components/NoDataOverlay/NoExperimentDataOverlay";
import { updateSitewideDashboardFilter } from "../../../../store/slices/sitewideDashboardFilters";
import { IAppState } from "../../../../store/types/store";
import { DashboardGraphs } from "../../DashboardGraphs";
import { haveSitewideFiltersBeenModified } from "../../Utils/utils";

import { SitewideDashboardHeader } from "./SitewideDashboardHeader";

export const SitewideDashboard = () => {
  const [initialLoad, setInitialLoad] = useImmer(true);
  const dashboardData: UseQueryResult<AnalyzeResourceResponseBodyType>[] =
    useSitewideAnalysis(getGraphConfig("alwaysOn"));
  const sitewideTotals: UseQueryResult<AnalyzeResourceResponseBodyType>[] =
    useSitewideAnalysis(getGraphConfig("alwaysOnCumulative"));

  const sitewideDashboardFilters = useSelector(
    (state: IAppState) => state.sitewideDashboardFilters
  );
  const shouldNotRenderGraphs = useMemo(() => {
    return (
      dashboardData &&
      dashboardData[0].isSuccess &&
      dashboardData[0].data?.metrics.length < 1 &&
      !haveSitewideFiltersBeenModified(sitewideDashboardFilters)
    );
  }, [dashboardData, sitewideDashboardFilters]);

  useEffect(() => {
    if (initialLoad) {
      setInitialLoad(false);
      updateSitewideDashboardFilter({
        key: "startTime",
        value: DateTime.now(),
      });
      updateSitewideDashboardFilter({
        key: "endTime",
        value: DateTime.now().minus({ days: 30 }).startOf("day"),
      });
    }
  }, [initialLoad]);

  return (
    <Grid container direction={"column"} rowSpacing={3}>
      <SitewideDashboardHeader
        latestRunTs={dashboardData[0]?.data?.latestRunTs ?? undefined}
      />
      {shouldNotRenderGraphs ? (
        <NoExperimentDataOverlay />
      ) : (
        <Grid item container direction={"column"}>
          <Grid item>
            <DashboardGraphs
              resourceType={"sitewide"}
              config={getGraphConfig("alwaysOn")}
              dashboardData={dashboardData}
              graphMetricTotals={filterDashboardData(
                sitewideTotals,
                "alwaysOnCumulative"
              )}
            />
          </Grid>
        </Grid>
      )}
    </Grid>
  );
};
